import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';
import '../../../Styles/ColoresCatalog.css';

// Configuración de endpoint de subida
const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/imagen-sistema`;

export default function ImagenesManagerModal({ open, mode, data, onClose, onSubmit, usedTypes = [] }) {
    const { getSystemConfig } = useLogos();
    const [tipos, setTipos] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // Upload states
    const [uploadError, setUploadError] = useState('');
    const [previousImagePath, setPreviousImagePath] = useState(null);

    const [form, setForm] = useState({
        nombre: '',
        descripcion: '',
        idTipoImagen: '',
        imagenUrl: '',
        imagenUrlPreview: '',
        activo: true
    });

    // Fetch TipoImagen
    useEffect(() => {
        if (open) {
            const fetchTypes = async () => {
                try {
                    const res = await axios.get(ENDPOINTS.tipoImagen);
                    const list = (res.data || []).map(t => ({
                        id: t.idTipoImagen || t.IdTipoImagen,
                        nombre: t.nombre || t.Nombre
                    }));
                    setTipos(list);
                } catch (e) {
                    console.error("Error loading TipoImagen:", e);
                }
            };
            fetchTypes();
        }
    }, [open]);

    // Init form
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && data) {
                const initialForm = {
                    nombre: data.nombre || '',
                    descripcion: data.descripcion || '',
                    idTipoImagen: data.idTipoImagen || '',
                    imagenUrl: data.imagenUrl || '',
                    imagenUrlPreview: data.imagenUrl || '',
                    activo: data.activo ?? true
                };
                setForm(initialForm);
                if (initialForm.imagenUrl) {
                    setPreviousImagePath(initialForm.imagenUrl);
                }
            } else {
                setForm({
                    nombre: '',
                    descripcion: '',
                    idTipoImagen: '',
                    imagenUrl: '',
                    imagenUrlPreview: '', // Reset preview
                    activo: true
                });
                setPreviousImagePath(null);
            }
            setUploadError('');
        }
    }, [open, mode, data]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validaciones
        if (!file.type.startsWith('image/')) {
            setUploadError('Solo se permiten archivos de imagen.');
            return;
        }
        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
        if (file.size > maxMB * 1024 * 1024) {
            setUploadError(`La imagen no debe superar los ${maxMB}MB.`);
            return;
        }

        setUploadError('');
        const blobUrl = URL.createObjectURL(file);

        // Preview inmediato
        setForm(prev => ({ ...prev, imagenUrlPreview: blobUrl }));
        setIsUploading(true);

        try {
            // Eliminar anterior si existe
            if (previousImagePath) {
                try {
                    await axios.delete(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`);
                } catch (ignore) {
                    console.warn("Could not delete previous image:", ignore);
                }
            }

            const formData = new FormData();
            formData.append('file', file);

            // Subir
            const response = await axios.post(UPLOAD_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const filePath = response.data.filePath || response.data.FilePath;

            setPreviousImagePath(filePath);
            setForm(prev => ({
                ...prev,
                imagenUrl: filePath,
                imagenUrlPreview: blobUrl
            }));

        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadError('Error al subir la imagen. Verifique su conexión.');
            setForm(prev => ({ ...prev, imagenUrlPreview: null }));
        } finally {
            setIsUploading(false);
        }
    };

    if (!open) return null;

    const isFormValid = form.nombre && form.idTipoImagen && form.imagenUrl && !isUploading;

    const labelStyle = {
        display: 'block',
        fontSize: '0.95rem',
        fontWeight: '800',
        color: '#0f172a',
        marginBottom: '0.5rem'
    };

    const Header = (
        <div
            className="roles-topbar"
            style={{
                marginBottom: '1.5rem',
                paddingRight: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: '0 0 auto',
            }}>
            <h3 className="roles-title" style={{ marginRight: 0 }}>
                {mode === 'new' ? 'Nueva Imagen' : 'Editar Imagen'}
            </h3>
            <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
                ✕
            </button>
        </div>
    );

    // Helper para URL de imagen
    const getImageUrl = () => {
        if (form.imagenUrlPreview && form.imagenUrlPreview.startsWith('blob:')) return form.imagenUrlPreview;

        if (form.imagenUrl) {
            return form.imagenUrl.startsWith('http')
                ? form.imagenUrl
                : `${SERVER_URL}${form.imagenUrl.startsWith('/') ? '' : '/'}${form.imagenUrl}`;
        }
        return null;
    };
    const currentImageUrl = getImageUrl();


    const Footer = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '.8rem',
                marginTop: 'auto',
                paddingTop: '2rem',
                paddingRight: '2rem',
                flex: '0 0 auto',
            }}
        >
            <button className="roles-btn" onClick={onClose}>Cancelar</button>
            <button
                className="btn-action"
                onClick={() => onSubmit(form, mode)}
                disabled={!isFormValid}
                style={{
                    opacity: !isFormValid ? 0.5 : 1,
                    cursor: !isFormValid ? 'not-allowed' : 'pointer',
                }}
            >
                {isUploading ? 'Subiendo...' : (mode === 'new' ? 'Guardar' : 'Actualizar')}
            </button>
        </div>
    );

    return (
        <div
            className="roles-confirm-overlay"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 1000,
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(500px, 94vw)',
                    maxHeight: '90vh',
                    background: '#fff',
                    borderRadius: 24,
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    paddingBottom: '1.5rem'
                }}
            >
                {Header}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                    <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        {mode === 'edit' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.25rem', background: '#f8fafc', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                    <div>
                                        <label style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.05em', display: 'block', marginBottom: '4px' }}>Tipo de Imagen</label>
                                        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>{tipos.find(t => Number(t.id) === Number(form.idTipoImagen))?.nombre || '---'}</span>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.05em', display: 'block', marginBottom: '4px' }}>Referencia</label>
                                        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>{form.nombre || '---'}</span>
                                    </div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.05em', display: 'block', marginBottom: '4px' }}>Descripción de Uso</label>
                                    <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#475569' }}>{form.descripcion || 'Sin descripción adicional'}</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Info Section */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={{ ...labelStyle, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Tipo de Imagen</label>
                                        <select
                                            className="infoField"
                                            value={form.idTipoImagen}
                                            onChange={(e) => handleChange('idTipoImagen', e.target.value)}
                                            style={{
                                                width: '100%',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                padding: '0.5rem',
                                                fontWeight: 600,
                                                color: '#1e293b'
                                            }}
                                        >
                                            <option value="">-- Seleccionar --</option>
                                            {tipos.map(t => {
                                                const isUsed = usedTypes.includes(Number(t.id));
                                                const isMyOriginalType = (mode === 'edit' && data && Number(data.idTipoImagen) === Number(t.id));
                                                if (isUsed && !isMyOriginalType) return null;
                                                return <option key={t.id} value={t.id}>{t.nombre}</option>;
                                            })}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ ...labelStyle, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Referencia</label>
                                        <input
                                            className="infoField"
                                            value={form.nombre}
                                            onChange={(e) => handleChange('nombre', e.target.value)}
                                            placeholder="Nombre de la imagen..."
                                            style={{
                                                width: '100%',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                padding: '0.5rem',
                                                fontWeight: 600,
                                                color: '#1e293b'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ ...labelStyle, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Descripción de Uso</label>
                                    <input
                                        className="infoField"
                                        value={form.descripcion}
                                        onChange={(e) => handleChange('descripcion', e.target.value)}
                                        placeholder="¿Dónde se utiliza esta imagen?"
                                        style={{
                                            width: '100%',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            padding: '0.5rem',
                                            color: '#64748b'
                                        }}
                                    />
                                </div>
                            </>
                        )}

                        <div style={{ height: '1px', background: '#f1f5f9', margin: '0.5rem 0' }}></div>

                        {/* Editable Section */}
                        <div>
                            <label style={{ ...labelStyle, color: '#1e293b', fontSize: '0.9rem' }}>Imagen del Sistema</label>
                            <div
                                className={`file-uploader${form.imagenUrl ? ' file-uploader--filled' : ''}${uploadError ? ' file-uploader--error' : ''}`}
                            >
                                {currentImageUrl && (
                                    <div className="file-uploader__preview">
                                        <img src={currentImageUrl} alt="Preview" onError={(e) => { e.target.style.display = 'none'; }} />
                                    </div>
                                )}

                                <label className="file-uploader__label">
                                    <input type="file" accept="image/*" onChange={handleFileChange} onClick={(e) => { e.target.value = ''; }} style={{ display: 'none' }} />
                                    <span className="file-uploader__icon">{form.imagenUrl ? '🖼️' : '📤'}</span>
                                    <span className="file-uploader__text">{form.imagenUrl ? 'Reemplazar Imagen' : 'Subir Imagen del Sistema'}</span>
                                    <span className="file-uploader__hint">PNG, JPG o WEBP (Máx. 5MB)</span>
                                </label>
                                {uploadError && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>{uploadError}</div>}
                            </div>
                        </div>
                    </div>
                </div>
                {Footer}
            </div>
        </div>
    );
}
